import React, { useRef, useState, useEffect } from "react";
import { Box, IconButton, Slider, Typography, Grid, Avatar, Stack, Menu, MenuItem } from "@mui/material";

// Icons
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import Replay10Icon from "@mui/icons-material/Replay10";
import Forward10Icon from "@mui/icons-material/Forward10";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import DownloadIcon from "@mui/icons-material/Download";
import SpeedIcon from "@mui/icons-material/Speed";

/**
 * Enhanced audio player with advanced controls
 *
 * Features:
 * - Skip forward/backward 10 seconds
 * - Save current playback position to browser history
 * - Restore playback position when revisiting
 * - Volume control
 * - Play/pause controls
 * - Time display
 * - Download option
 */
export default function EnhancedAudioPlayer(props) {
    const { audioSrc, audioType = "audio/mpeg", id = null } = props;

    // Refs
    const audioRef = useRef(null);

    // States
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [isMuted, setIsMuted] = useState(false);
    const [playbackSpeed, setPlaybackSpeed] = useState(1);
    const [playbackMenuAnchorEl, setPlaybackMenuAnchorEl] = useState(null);

    const volume = props.browser?.audioPlayerVolume;
    const setVolume = props.browser?.setAudioPlayerVolume;

    // Initialize audio player
    useEffect(() => {
        if (!audioRef.current) return;

        const audio = audioRef.current;

        // Set startup volume
        audio.volume = volume;

        // Set up event listeners
        const handleTimeUpdate = () => {
            setCurrentTime(audio.currentTime);
            // Save timestamp every 5 seconds
            if (Math.floor(audio.currentTime) % 5 === 0) {
                setAudioPlayerTimestamp(audio.currentTime);
            }
        };

        const handleDurationChange = () => {
            setDuration(audio.duration);
        };

        const handleEnded = () => {
            setIsPlaying(false);
        };

        const handlePlay = () => {
            setIsPlaying(true);
        };

        const handlePause = () => {
            setIsPlaying(false);
        };

        // Add event listeners
        audio.addEventListener("timeupdate", handleTimeUpdate);
        audio.addEventListener("durationchange", handleDurationChange);
        audio.addEventListener("ended", handleEnded);
        audio.addEventListener("play", handlePlay);
        audio.addEventListener("pause", handlePause);

        // Remove event listeners on cleanup
        return () => {
            audio.removeEventListener("timeupdate", handleTimeUpdate);
            audio.removeEventListener("durationchange", handleDurationChange);
            audio.removeEventListener("ended", handleEnded);
            audio.removeEventListener("play", handlePlay);
            audio.removeEventListener("pause", handlePause);
        };
    }, []);

    // Restore last playback position
    // Only restore if we have a valid time and audio is loaded
    useEffect(() => {
        if (!audioRef.current || !audioSrc) return;

        const savedTime = getAudioPlayerTimestamp();

        if (savedTime) {
            if (!isNaN(savedTime) && audioRef.current.readyState >= 2) {
                audioRef.current.currentTime = savedTime;
                console.log(`Restored playback position to ${formatTime(savedTime)}`);
            }
        }
    }, [audioSrc, duration]);

    // Format time as MM:SS
    const formatTime = React.useCallback((timeInSeconds) => {
        if (isNaN(timeInSeconds)) return "00:00:00";

        const hours = Math.floor(timeInSeconds / 3600);
        const minutes = Math.floor((timeInSeconds % 3600) / 60);
        const seconds = Math.floor(timeInSeconds % 60);

        if (hours > 0) {
            return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
        } else {
            return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
        }
    }, []);

    // Functions for timestamp management
    const getAudioPlayerTimestamp = React.useCallback(() => {
        if (!id) return null;
        const timestamps = props.browser?.audioPlayerTimestamps;
        return timestamps?.[id] ?? null;
    }, [id, props.browser?.audioPlayerTimestamps]);

    const setAudioPlayerTimestamp = React.useCallback(
        (ts) => {
            if (!id) return;
            const setTimestamps = props.browser?.setAudioPlayerTimestamps;
            if (setTimestamps) {
                setTimestamps((prevTimestamps) => ({
                    ...prevTimestamps,
                    [id]: ts
                }));
            }
        },
        [id, props.browser?.setAudioPlayerTimestamps]
    );

    // Handle play/pause toggle
    const handlePlayPause = () => {
        const audio = audioRef.current;
        if (!audio) return;

        if (isPlaying) {
            audio.pause();
        } else {
            audio.play();
        }
    };

    // Skip forward 10 seconds
    const handleSkipForward = () => {
        if (!audioRef.current) return;
        audioRef.current.currentTime = Math.min(audioRef.current.currentTime + 10, duration);
    };

    // Skip backward 10 seconds
    const handleSkipBackward = () => {
        if (!audioRef.current) return;
        audioRef.current.currentTime = Math.max(audioRef.current.currentTime - 10, 0);
    };

    // Handle time slider change
    const handleTimeSliderChange = (e, newValue) => {
        if (!audioRef.current) return;
        audioRef.current.currentTime = newValue;
        setCurrentTime(newValue);
    };

    // Handle volume slider change
    const handleVolumeChange = (e, newValue) => {
        if (!audioRef.current) return;

        const volumeValue = newValue / 100;
        audioRef.current.volume = volumeValue;
        setVolume(volumeValue);

        if (volumeValue === 0) {
            audioRef.current.muted = true;
            setIsMuted(true);
        } else {
            audioRef.current.muted = false;
            setIsMuted(false);
        }
    };

    // Handle volume icon click to toggle mute
    const handleVolumeIconClick = (e) => {
        e.stopPropagation(); // Stop the event from bubbling to parent elements

        // Toggle mute state
        if (audioRef.current) {
            const newMuteState = !isMuted;
            audioRef.current.muted = newMuteState;
            setIsMuted(newMuteState);
        }
    };

    // Open playback rate menu
    const handlePlaybackRateMenuOpen = (event) => {
        setPlaybackMenuAnchorEl(event.currentTarget);
    };

    // Close playback rate menu
    const handlePlaybackRateMenuClose = () => {
        setPlaybackMenuAnchorEl(null);
    };

    // Handle speed selection
    const handlePlaybackRateChange = (speed) => {
        if (!audioRef.current) return;

        setPlaybackSpeed(speed);
        audioRef.current.playbackRate = speed;
        handlePlaybackRateMenuClose();
    };

    // Apply playback speed when audio element changes or speed changes
    useEffect(() => {
        if (!audioRef.current) return;
        audioRef.current.playbackRate = playbackSpeed;
    }, [playbackSpeed]);

    return (
        <Box sx={{ p: 1 }}>
            {/* Hidden audio element */}
            <audio ref={audioRef} src={audioSrc} type={audioType} preload="metadata" />

            {/* Time slider */}
            <Box sx={{ width: "100%", mb: 1 }}>
                <Slider
                    size="small"
                    value={currentTime}
                    min={0}
                    max={duration || 100}
                    onChange={handleTimeSliderChange}
                    sx={{
                        height: 6,
                        "& .MuiSlider-thumb": {
                            width: 14,
                            height: 14,
                            transition: "0.3s cubic-bezier(.47,1.64,.41,.8)",
                            "&:hover, &.Mui-focusVisible": {
                                boxShadow: "0px 0px 0px 8px rgba(25, 118, 210, 0.16)"
                            }
                        }
                    }}
                />
            </Box>

            {/* Controls */}
            <Grid container justifyContent="center" alignItems="center" spacing={1}>
                <Grid item xs={2} textAlign="left">
                    <Typography variant="caption" sx={{ fontFamily: "monospace" }}>
                        {formatTime(currentTime)}
                    </Typography>
                </Grid>

                <Grid item xs={8}>
                    <Stack direction="row" justifyContent="center" spacing={1}>
                        <IconButton onClick={handleSkipBackward} size="small">
                            <Replay10Icon />
                        </IconButton>

                        <Avatar>
                            <IconButton onClick={handlePlayPause} size="medium">
                                {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
                            </IconButton>
                        </Avatar>

                        <IconButton onClick={handleSkipForward} size="small">
                            <Forward10Icon />
                        </IconButton>
                    </Stack>
                </Grid>

                <Grid item xs={2} textAlign="right">
                    <Typography variant="caption" sx={{ fontFamily: "monospace" }}>
                        {formatTime(duration)}
                    </Typography>
                </Grid>
            </Grid>

            {/* Duration and volume */}
            <Grid container justifyContent="center" alignItems="center" spacing={1} mt={2}>
                <Grid item xs={2} sm={1} textAlign="right">
                    <IconButton size="small" onClick={handleVolumeIconClick}>
                        {isMuted ? <VolumeOffIcon /> : <VolumeUpIcon />}
                    </IconButton>
                </Grid>

                <Grid item xs={5} sm={3}>
                    <Slider
                        size="small"
                        color="secondary"
                        value={isMuted ? 0 : volume * 100}
                        onChange={handleVolumeChange}
                        sx={{
                            height: 4,
                            "& .MuiSlider-thumb": {
                                width: 10,
                                height: 10
                            }
                        }}
                    />
                </Grid>

                <Grid item xs={2} sm={2} container spacing={1} justifyContent="center">
                    <Grid item>
                        <IconButton size="small" onClick={handlePlaybackRateMenuOpen} color={playbackSpeed !== 1 ? "primary" : "default"}>
                            <SpeedIcon />
                        </IconButton>

                        <Menu anchorEl={playbackMenuAnchorEl} open={Boolean(playbackMenuAnchorEl)} onClose={handlePlaybackRateMenuClose}>
                            {[0.5, 0.75, 0.9, 1, 1.1, 1.25, 1.5, 1.75, 2].map((rate) => (
                                <MenuItem key={rate} onClick={() => handlePlaybackRateChange(rate)} disabled={rate === playbackSpeed}>
                                    {rate}x
                                </MenuItem>
                            ))}
                        </Menu>
                    </Grid>

                    <Grid item>
                        <IconButton size="small" component="a" href={audioSrc} download>
                            <DownloadIcon />
                        </IconButton>
                    </Grid>
                </Grid>
            </Grid>
        </Box>
    );
}
